import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Typography, FormControl, InputLabel,
    Select, MenuItem, InputAdornment, IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Colaborador from '../../types/negocio/Colaborador';
import { Departamento } from '../../types/negocio/Departamento';
import BasicModal from './BasicModal';
import SelectPerfil from '../SelectPerfil';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave?: (data: Colaborador) => void;
    colaborador: Colaborador;
    departamentos: Departamento[];
    modoEdicao?: boolean;
    setColaborador?: React.Dispatch<React.SetStateAction<Colaborador>>;
    onEditClick?: () => void;
    isNovoColaborador?: boolean;
}

const ColaboradorModal: React.FC<Props> = ({
    open,
    onClose,
    onSave,
    colaborador,
    departamentos,
    modoEdicao = false,
    setColaborador,
    onEditClick,
    isNovoColaborador = false
}) => {
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);

    // Lista de campos obrigatórios
    const camposObrigatorios = ['nomeCompleto', 'email', 'cargoAtual', 'telefone', 'senha'];

    // Validar campos obrigatórios
    const validarCampos = (): boolean => {
        const novosErros: Record<string, boolean> = {};
        
        if (modoEdicao && isNovoColaborador) {
            for (const campo of camposObrigatorios) {
                const valor = colaborador[campo as keyof Colaborador];
                if (!valor || (typeof valor === 'string' && valor.trim() === '')) {
                    novosErros[campo] = true;
                }
            }
        }
        
        setErrors(novosErros);
        return Object.keys(novosErros).length === 0;
    };

    // Limpar erros quando o modal fechar
    useEffect(() => {
        if (!open) {
            setErrors({});
        }
    }, [open]);

    const getModalTitle = () => {
        if (modoEdicao) {
            return isNovoColaborador ? "Adicionar Colaborador" : "Editar Colaborador";
        }
        return "Detalhes do Colaborador";
    };

    const handleChange = (field: keyof Colaborador, value: string) => {
        if (!setColaborador) return;
        setColaborador(prev => ({ ...prev, [field]: value }));
        
        // Limpar erro do campo quando ele for preenchido
        if (errors[field] && value.trim() !== '') {
            setErrors(prev => ({ ...prev, [field]: false }));
        }
    };

    const renderCampo = (label: string, field: keyof Colaborador, multiline = false) => {
        const isObrigatorio = modoEdicao && isNovoColaborador && camposObrigatorios.includes(field);
        const temErro = errors[field] || false;
        
        // Campo especial: departamento (Select)
        if (field === 'departamento') {
            const nomeDepartamento = (
                departamentos.find(d => d.guid === colaborador.departamento)?.nome
                || 'Sem departamento'
            );

            if (!modoEdicao && (!colaborador.departamento || colaborador.departamento.trim() === '')) return null;

            return (
                <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
                    {modoEdicao ? (
                        <FormControl fullWidth variant="outlined" color="secondary">
                            <InputLabel>Departamento</InputLabel>
                            <Select
                                value={colaborador.departamento || ''}
                                onChange={(e) => handleChange('departamento', e.target.value)}
                                label="Departamento"
                            >
                                <MenuItem value="">
                                    <em>Selecione um departamento</em>
                                </MenuItem>
                                {departamentos.map((dep: Departamento) => (
                                    <MenuItem key={dep.guid} value={dep.guid}>
                                        {dep.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>
                            <Typography fontSize={12} sx={{ color: "#607C90" }}>
                                {label}
                            </Typography>
                            <Typography
                                fontSize={16}
                                sx={{
                                    color: !colaborador.departamento || !departamentos.find(d => d.guid === colaborador.departamento)
                                        ? (theme) => theme.palette.secondary.main
                                        : 'black'
                                }}
                            >
                                {nomeDepartamento}
                            </Typography>
                        </>
                    )}
                </Box>
            );
        }

        // Campo especial: senha (TextField com visibilidade)
        if (field === 'senha') {
            if (!modoEdicao && (!colaborador.senha || colaborador.senha.trim() === '')) return null;

            return (
                <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
                    {modoEdicao ? (
                        <TextField
                            fullWidth
                            label={label}
                            variant="outlined"
                            color="secondary"
                            type={showPassword ? 'text' : 'password'}
                            value={colaborador.senha || ''}
                            onChange={(e) => handleChange('senha', e.target.value)}
                            error={temErro}
                            helperText={temErro ? 'Este campo é obrigatório' : ''}
                            required={isObrigatorio}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    ) : (
                        <>
                            <Typography fontSize={12} sx={{ color: "#607C90" }}>
                                {label}
                            </Typography>
                            <Typography fontSize={16}>
                                {'••••••••'}
                            </Typography>
                        </>
                    )}
                </Box>
            );
        }

        // Campo especial: perfil (SelectPerfil)
        if (field === 'perfilGuid') {
            if (!modoEdicao && (!colaborador.perfilGuid || colaborador.perfilGuid.trim() === '')) return null;

            return (
                <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
                    {modoEdicao ? (
                        <SelectPerfil
                            value={colaborador.perfilGuid || ''}
                            onChange={(perfil) => {
                                if (!setColaborador) return;
                                setColaborador(prev => ({
                                    ...prev,
                                    perfilGuid: perfil?.guid || '',
                                    perfil: perfil
                                }));
                            }}
                            label="Perfil de Acesso"
                            error={temErro}
                            helperText={temErro ? 'Este campo é obrigatório' : ''}
                            required={isObrigatorio}
                        />
                    ) : (
                        <>
                            <Typography fontSize={12} sx={{ color: "#607C90" }}>
                                Perfil de Acesso
                            </Typography>
                            <Typography fontSize={16}>
                                {colaborador.perfil?.nome || 'Sem perfil'}
                            </Typography>
                        </>
                    )}
                </Box>
            );
        }

        const valor = colaborador[field];
        if (!modoEdicao && (!valor || valor.toString().trim() === '')) return null;

        return (
            <Box sx={{ width: modoEdicao ? '100%' : '50%', px: 1, mb: 2 }}>
                {modoEdicao ? (
                    <TextField
                        fullWidth
                        label={label}
                        variant="outlined"
                        color="secondary"
                        value={valor || ''}
                        onChange={(e) => handleChange(field, e.target.value)}
                        multiline={multiline}
                        rows={multiline ? 2 : 1}
                        error={temErro}
                        helperText={temErro ? 'Este campo é obrigatório' : ''}
                        required={isObrigatorio}
                    />
                ) : (
                    <>
                        <Typography fontSize={12} sx={{ color: "#607C90" }}>
                            {label}
                        </Typography>
                        <Typography fontSize={16}>
                            {typeof valor === 'object' ? JSON.stringify(valor) : valor?.toString() || ''}
                        </Typography>
                    </>
                )}
            </Box>
        );
    };

    return (
        <BasicModal
            title={getModalTitle()}
            open={open}
            onClose={onClose}
            saveButton={modoEdicao}
            onSave={async () => {
                if (modoEdicao && isNovoColaborador) {
                    if (!validarCampos()) return;
                }
                if (!onSave) return;
                setSaving(true);
                try {
                    await onSave(colaborador);
                } finally {
                    setSaving(false);
                }
            }}
            saveLoading={saving}
            onEdit={onEditClick}
            tela='colaboradores'
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                {renderCampo('Nome Completo', 'nomeCompleto')}
                {renderCampo('Email', 'email')}
                {renderCampo('Cargo Atual', 'cargoAtual')}
                {renderCampo('Departamento', 'departamento')}
                {renderCampo('Telefone', 'telefone')}
                {renderCampo('Senha', 'senha')}
                {renderCampo('Perfil de Acesso', 'perfilGuid')}
                {renderCampo('Admissão', 'admissao')}
                {renderCampo('Formação Acadêmica', 'formacaoAcademica')}
                {renderCampo('Certificações', 'certificacoes')}
                {renderCampo('Experiência Profissional', 'experienciaProfissional', true)}
                {renderCampo('Idiomas', 'idiomas')}
            </Box>
        </BasicModal>
    );
};

export default ColaboradorModal;
